import { isEmpty } from 'lodash';
import { BillItem } from '../../types/billing';
import {
  getSelectedPackageId,
  getSubscriptionCode,
  getSubscriptionCodeValidationError,
} from '../../web-shared/redux/selectors/shared-billing';
import { Channel, NULL_PLAN, Packages } from '../../web-shared/types/billing';
import { RootState } from '../reducers';
import { getAllInputsValid } from './auth';

function getIsLinkedOffer({ billing }: RootState) {
  return billing.isLinkedOffer;
}

export function getHideSubscriptionCodeToggleLink(state: RootState) {
  const subscriptionCode = getSubscriptionCode(state);
  const isLinkedOffer = getIsLinkedOffer(state);
  const hasErrorMessage = !!getSubscriptionCodeValidationError(state);
  return isLinkedOffer && !!subscriptionCode && !hasErrorMessage;
}

export function getShowSubscriptionCodeField({ billing }: RootState) {
  return billing.showSubscriptionCodeField;
}

export function getSubscriptionCodeInput({ billing }: RootState) {
  return billing.subscriptionCodeInput;
}

export function getIsAuthenticated({ billing }: RootState) {
  return billing.isAuthenticated;
}

export function getPackageDetailsAreLoaded({ billing }: RootState) {
  return billing.packageDetailsAreLoaded;
}

export function getIsLoadingPackageDetails({ billing }: RootState) {
  return billing.isLoadingPackageDetails;
}

export function getBillingErrorMessage({ billing }: RootState) {
  return billing.billingErrorMessage;
}

export function getSelectablePlans({ billing }: RootState) {
  return billing.selectablePlans;
}

export function getIsLoadingSubscriptionStart({ billing }: RootState) {
  return billing.isLoadingSubscriptionStart;
}

export function getPlusPlan(state: RootState) {
  const selectablePlans = getSelectablePlans(state);
  return selectablePlans.find(plan => plan.id === Packages.PHILO_PLUS) || NULL_PLAN;
}

export function getPhilo2021Plan(state: RootState) {
  const selectablePlans = getSelectablePlans(state);
  return selectablePlans.find(plan => plan.id === Packages.PHILO_2021) || NULL_PLAN;
}

export function getPhiloBundlePlusPlan(state: RootState) {
  const selectablePlans = getSelectablePlans(state);
  return selectablePlans.find(plan => plan.id === Packages.PHILO_ARGON) || NULL_PLAN;
}

export function getPromotionalItems({ billing }: RootState) {
  return billing.promotionalItems;
}

export function getDefaultPlan(state: RootState): BillItem {
  const plusPlan = getPlusPlan(state);
  const philo2021Plan = getPhilo2021Plan(state);
  const philoBundlePlusPlan = getPhiloBundlePlusPlan(state);
  return (
    (!isEmpty(plusPlan) && plusPlan) ||
    (!isEmpty(philo2021Plan) && philo2021Plan) ||
    (!isEmpty(philoBundlePlusPlan) && philoBundlePlusPlan) ||
    NULL_PLAN
  );
}

export function getSelectedPlan(state: RootState) {
  const selectedPackageId = getSelectedPackageId(state);
  const selectablePlans = getSelectablePlans(state);
  const promotionalItems = getPromotionalItems(state);
  const defaultPlan = getDefaultPlan(state);

  return (
    selectablePlans.find(plan => plan.id === selectedPackageId) ||
    promotionalItems.find(plan => plan.id === selectedPackageId) ||
    defaultPlan
  );
}

export function getAddOns({ billing }: RootState) {
  return billing.addOns;
}

export function getSelectedAddOns({ billing }: RootState) {
  return billing.selectedAddOns;
}

export function getAddOnPromoDetails({ billing }: RootState) {
  return billing.addOnPromoDetails;
}

export function getNeedsValidPaymentInfo({ billing }: RootState) {
  return billing.needsValidPaymentInfo;
}

export function getCanProvidePayment({ billing }: RootState) {
  return billing.canProvidePayment;
}

export function getIsSubscribed({ billing }: RootState) {
  return billing.isSubscribed;
}

export function getSignupStep({ billing: { signupStep } }: RootState) {
  return signupStep;
}

export function getHasAgreedToSignupTerms(state: RootState) {
  return state.billing.hasAgreedToSignupTerms;
}

export function getIsSignupInfoComplete(state: RootState) {
  return getAllInputsValid(state) || state.config.isUserLoggedIn;
}

export function getCanAgreeToSignupTerms(state: RootState) {
  return getCanProvidePayment(state) && getIsSignupInfoComplete(state);
}

export function getIsPaymentEnabled(state: RootState) {
  return getCanAgreeToSignupTerms(state) && getHasAgreedToSignupTerms(state);
}

export function getAddOnsChannels(state: RootState) {
  const addOns = getAddOns(state);
  return addOns.reduce<BillItem['channels']>(
    (acc, { channels: addOnChannels }) => acc.concat(...addOnChannels),
    [],
  );
}

export function getAllChannels(state: RootState) {
  const { channels = [], freeChannels = [] } = getDefaultPlan(state);
  const addOnsChannels = getAddOnsChannels(state);
  return [...channels, ...freeChannels, ...addOnsChannels];
}

export function getChannelsMap(state: RootState) {
  const allChannels = getAllChannels(state);
  return allChannels.reduce<Record<string, Channel>>((acc, channel) => {
    acc[channel.name] = channel;
    return acc;
  }, {});
}

export function getHasAgreedToPaymentTerms({ billing }: RootState) {
  return billing.hasAgreedToPaymentTerms;
}

export function getIsFASTSignUp({ billing }: RootState) {
  return billing.isFASTSignUp;
}
